import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Chip,
    RadioGroup,
    Radio,
    Checkbox,
    OutlinedInput,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Axios from "axios";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { getAuthHeader } from "../../utils/authToken";
import ReportBatchesPanel from "./ReportBatchesPanel";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;
const authCfg = () => ({ headers: { Authorization: getAuthHeader() } });
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CADENCE_OPTS = [
    { value: "WEEKLY", label: "Weekly" },
    { value: "DAILY", label: "Daily" },
    { value: "CUSTOM_DATES", label: "Specific dates" },
];

// Next few send moments, computed client-side for the caption (the server's isReportDueNow
// is authoritative; this only previews the schedule the admin is editing).
const nextSends = (s, count = 3) => {
    if (!s.enabled) return [];
    const hm = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(s.send_time || "");
    const [h, m] = hm ? [Number(hm[1]), Number(hm[2])] : [8, 30];
    const days = (s.days_of_week || []).map(Number);
    const now = DateTime.now().setZone("America/New_York");
    const out = [];
    let cursor = now.startOf("day");
    for (let i = 0; i < 400 && out.length < count; i += 1) {
        const dow = cursor.weekday % 7;
        const dateOk = s.cadence === "DAILY"
            || (s.cadence === "WEEKLY" && days.includes(dow))
            || (s.cadence === "CUSTOM_DATES" && (s.send_dates || []).includes(cursor.toISODate()));
        if (dateOk) {
            const at = cursor.set({ hour: h, minute: m });
            if (at > now) out.push(at);
        }
        cursor = cursor.plus({ days: 1 });
    }
    return out;
};
const PARENT_MODES = [
    { value: "both", label: "Both parent emails" },
    { value: "primary", label: "Primary parent only" },
    { value: "secondary", label: "Secondary parent only" },
];

// Reports wizard step: configure the recurring progress report. Saves to
// PUT /prep-programs/:uuid/report-schedule. recommendedDay (0–6) defaults the weekday to
// the homework-round release day when known.
const ReportScheduleSection = ({ programUuid, recommendedDay = null }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [s, setS] = useState({
        enabled: false,
        cadence: "WEEKLY",
        days_of_week: [recommendedDay ?? 1],
        send_dates: [],
        send_time: "08:30",
        delivery_mode: "AUTO",
        email_student: true,
        email_parent: true,
        parent_email_mode: "both",
        // V2 (AI summary on) matches what the cron has always sent — V1 here would silently
        // turn the summary off for every new program.
        parent_report_version: "V2",
        email_admin: true,
        admin_emails: "",
    });
    const [dateDraft, setDateDraft] = useState("");
    // Homework recurrence cadences, for the "reports more frequent than homework" advisory.
    const [homeworkRules, setHomeworkRules] = useState([]);
    // Content scope (§8): which exams/library tests the SCHEDULED report covers, and whether
    // homework is included. Defaults = today's behavior (all exams, no library tests, homework on).
    const [content, setContent] = useState({ exams_mode: "ALL", exam_ids: [], v2_mode: "ALL", v2_test_ids: [], include_homework: true, homework_time: false });
    const [programExams, setProgramExams] = useState([]);
    const [programV2Tests, setProgramV2Tests] = useState([]);
    const setC = (patch) => setContent((prev) => ({ ...prev, ...patch }));

    // Preview dialog state.
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewTab, setPreviewTab] = useState(0);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    // Resolved default office address (server env PREP_REPORT_ADMIN_EMAIL) used when the
    // admin-emails field is blank; null = not configured.
    const [defaultAdminEmail, setDefaultAdminEmail] = useState(null);

    const set = (patch) => setS((prev) => ({ ...prev, ...patch }));

    const load = useCallback(async () => {
        if (!programUuid) return;
        setLoading(true);
        try {
            const res = await Axios.get(`${BASE}/${programUuid}/report-schedule`, authCfg());
            // Response is { schedule: row|null, default_admin_email }: config kept separate from state.
            setDefaultAdminEmail(res.data?.default_admin_email ?? null);
            const sched = res.data?.schedule;
            if (sched) {
                set({
                    enabled: !!sched.enabled,
                    cadence: sched.cadence || "WEEKLY",
                    days_of_week: Array.isArray(sched.days_of_week) && sched.days_of_week.length
                        ? sched.days_of_week
                        : [sched.day_of_week ?? recommendedDay ?? 1],
                    send_dates: Array.isArray(sched.send_dates) ? sched.send_dates : [],
                    send_time: sched.send_time || "08:30",
                    delivery_mode: sched.delivery_mode || "AUTO",
                    email_student: sched.email_student !== false,
                    email_parent: sched.email_parent !== false,
                    parent_email_mode: sched.parent_email_mode || "both",
                    parent_report_version: sched.parent_report_version || "V2",
                    email_admin: sched.email_admin !== false,
                    admin_emails: sched.admin_emails || "",
                });
                const cs = sched.content_selection;
                // v2_test_ids: null/absent = ALL (the default — future tests join
                // automatically), [] = none, [ids] = selected.
                const v2 = cs ? cs.v2_test_ids : null;
                setContent({
                    exams_mode: cs && Array.isArray(cs.exam_ids) ? "SELECTED" : "ALL",
                    exam_ids: (cs && cs.exam_ids) || [],
                    v2_mode: !Array.isArray(v2) ? "ALL" : (v2.length ? "SELECTED" : "NONE"),
                    v2_test_ids: Array.isArray(v2) ? v2 : [],
                    include_homework: !cs || cs.homework !== "none",
                    homework_time: !!(cs && cs.homework_time),
                });
            }
        } catch (e) {
            console.error("Load report schedule error:", e);
        } finally {
            setLoading(false);
        }
        // Pickers for the content scope — best-effort.
        try {
            const [ex, v2] = await Promise.all([
                Axios.get(`${BASE}/${programUuid}/exams`, authCfg()),
                Axios.get(`${BASE}/${programUuid}/v2-tests`, authCfg()),
            ]);
            setProgramExams(ex.data || []);
            setProgramV2Tests(v2.data || []);
        } catch (e) {
            setProgramExams([]);
            setProgramV2Tests([]);
        }
        // Homework cadence context for the mismatch advisory — best-effort, older servers 404.
        try {
            const rules = await Axios.get(`${BASE}/${programUuid}/homework-recurrences`, authCfg());
            setHomeworkRules(rules.data || []);
        } catch (e) {
            setHomeworkRules([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programUuid]);

    useEffect(() => {
        load();
    }, [load]);

    // Revoke the PDF object URL on unmount (or when it's replaced) so it doesn't leak if the
    // dialog is open when this step unmounts. closePreview also revokes on explicit close.
    useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

    const save = async () => {
        setSaving(true);
        try {
            await Axios.put(`${BASE}/${programUuid}/report-schedule`, {
                enabled: s.enabled,
                cadence: s.cadence,
                days_of_week: s.cadence === "WEEKLY" ? s.days_of_week : [],
                day_of_week: s.cadence === "WEEKLY" ? (s.days_of_week[0] ?? null) : null, // legacy mirror
                send_dates: s.cadence === "CUSTOM_DATES" ? s.send_dates : [],
                send_time: s.send_time || null,
                delivery_mode: s.delivery_mode,
                // All-defaults → null, so the default fast path stays stored as NULL
                // (exams ALL + library ALL + homework on).
                content_selection:
                    content.exams_mode === "ALL" && content.v2_mode === "ALL" && content.include_homework
                        && !content.homework_time
                        ? null
                        : {
                            exam_ids: content.exams_mode === "ALL" ? null : content.exam_ids,
                            // null = ALL, [] = none, [ids] = selected.
                            v2_test_ids: content.v2_mode === "ALL" ? null
                                : (content.v2_mode === "SELECTED" && content.v2_test_ids.length ? content.v2_test_ids : []),
                            homework: content.include_homework ? "current_round" : "none",
                            homework_time: !!content.homework_time,
                        },
                email_student: s.email_student,
                email_parent: s.email_parent,
                parent_email_mode: s.parent_email_mode,
                parent_report_version: s.parent_report_version,
                email_admin: s.email_admin,
                admin_emails: s.admin_emails.trim(),
            }, authCfg());
            enqueueSnackbar("Report schedule saved", { variant: "success" });
        } catch (e) {
            console.error("Save report schedule error:", e);
            enqueueSnackbar(e.response?.data?.error || "Failed to save report schedule", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    // --- Preview ---------------------------------------------------------------
    const loadEmailPreview = useCallback(async () => {
        setPreviewLoading(true);
        setPreviewError(false);
        try {
            const res = await Axios.get(`${BASE}/${programUuid}/report-preview`, authCfg());
            setPreviewData(res.data);
        } catch (e) {
            console.error("Report preview error:", e);
            setPreviewError(true); // shown in-dialog with a Retry, not a transient snackbar
        } finally {
            setPreviewLoading(false);
        }
    }, [programUuid]);

    const loadPdfPreview = useCallback(async () => {
        if (pdfUrl) return;
        setPdfLoading(true);
        try {
            const res = await Axios.get(`${BASE}/${programUuid}/report-preview/pdf`, { ...authCfg(), responseType: "blob" });
            setPdfUrl(URL.createObjectURL(res.data));
        } catch (e) {
            console.error("Report PDF preview error:", e);
            enqueueSnackbar("Failed to load PDF preview", { variant: "error" });
        } finally {
            setPdfLoading(false);
        }
    }, [programUuid, pdfUrl, enqueueSnackbar]);

    const openPreview = () => {
        setPreviewOpen(true);
        setPreviewTab(0);
        loadEmailPreview();
    };

    const closePreview = () => {
        setPreviewOpen(false);
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setPreviewData(null);
        setPreviewError(false);
    };

    const onPreviewTab = (_e, v) => {
        setPreviewTab(v);
        if (v === 3) loadPdfPreview();
    };

    // Recipient lists computed from the CURRENT (possibly unsaved) toggles + the sample
    // student's addresses — mirrors the server's studentRecipients/parentRecipients (incl. the
    // shared-address dedup) so the admin sees how their choices map to real recipients.
    const sample = previewData?.sample;
    const studentRecips = (s.email_student && sample?.student_email) ? [sample.student_email] : [];
    const parentRecips = (() => {
        if (!s.email_parent || !sample) return [];
        const to = [];
        if ((s.parent_email_mode === "both" || s.parent_email_mode === "primary") && sample.parent_email) to.push(sample.parent_email);
        if ((s.parent_email_mode === "both" || s.parent_email_mode === "secondary") && sample.parent_email_2) to.push(sample.parent_email_2);
        // Shared address → student copy only, deduped case-insensitively (mirrors the server send).
        const studentLower = new Set(studentRecips.map((a) => a.toLowerCase()));
        return [...new Set(to)].filter((a) => !studentLower.has(a.toLowerCase()));
    })();
    const adminRecipientsText = !s.email_admin
        ? "(admins off)"
        : (s.admin_emails.trim() || defaultAdminEmail || "no default office address configured");

    if (!programUuid) {
        return <Typography variant="body2" color="text.secondary">Create the program first to set up reports.</Typography>;
    }
    if (loading) return <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 560 }}>
            <Typography variant="body2" color="text.secondary">
                Each student gets a progress report — practice tests taken, focus chapters to work on, and
                question-bank homework progress — generated on a recurring day and emailed to the recipients below.
            </Typography>

            <FormControlLabel
                control={<Switch checked={s.enabled} onChange={(e) => set({ enabled: e.target.checked })} />}
                label="Enable recurring reports"
            />

            <Box sx={{ opacity: s.enabled ? 1 : 0.5, pointerEvents: s.enabled ? "auto" : "none" }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <FormControl size="small" sx={{ width: 180 }}>
                        <InputLabel>Cadence</InputLabel>
                        <Select label="Cadence" value={s.cadence} onChange={(e) => set({ cadence: e.target.value })}>
                            {CADENCE_OPTS.map((c) => (
                                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField label="Send time (ET)" type="time" size="small" sx={{ width: 150 }} InputLabelProps={{ shrink: true }}
                        value={s.send_time} onChange={(e) => set({ send_time: e.target.value })} />
                </Box>

                {s.cadence === "WEEKLY" && (
                    <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        {DOW_SHORT.map((d, i) => {
                            const on = (s.days_of_week || []).includes(i);
                            return (
                                <Chip key={i} label={d} size="small" clickable
                                    color={on ? "primary" : "default"}
                                    variant={on ? "filled" : "outlined"}
                                    onClick={() => set({
                                        days_of_week: on
                                            ? s.days_of_week.filter((x) => x !== i)
                                            : [...s.days_of_week, i].sort((a, b) => a - b),
                                    })} />
                            );
                        })}
                    </Box>
                )}

                {s.cadence === "CUSTOM_DATES" && (
                    <Box sx={{ mt: 1.5 }}>
                        <Box display="flex" gap={1} alignItems="center">
                            <TextField label="Add a date" type="date" size="small" InputLabelProps={{ shrink: true }}
                                value={dateDraft} onChange={(e) => setDateDraft(e.target.value)} />
                            <Button size="small" variant="outlined"
                                disabled={!dateDraft || s.send_dates.includes(dateDraft)}
                                onClick={() => {
                                    set({ send_dates: [...s.send_dates, dateDraft].sort() });
                                    setDateDraft("");
                                }}>
                                Add
                            </Button>
                        </Box>
                        <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                            {s.send_dates.map((d) => (
                                <Chip key={d} label={d} size="small"
                                    color={d < DateTime.now().setZone("America/New_York").toISODate() ? "default" : "primary"}
                                    variant="outlined"
                                    onDelete={() => set({ send_dates: s.send_dates.filter((x) => x !== d) })} />
                            ))}
                            {s.send_dates.length === 0 && (
                                <Typography variant="caption" color="text.secondary">No dates yet — add at least one future date.</Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {s.enabled && (() => {
                    const upcoming = nextSends(s);
                    return upcoming.length ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            Next sends: {upcoming.map((d) => d.toFormat("EEE M/d h:mma")).join(", ")}
                        </Typography>
                    ) : null;
                })()}
                {s.enabled && s.cadence === "WEEKLY" && recommendedDay != null && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        Recommended day: {DAYS[recommendedDay]} — your homework-round release day, so reports land with new homework.
                    </Typography>
                )}
                {s.enabled && s.cadence === "DAILY" && homeworkRules.length > 0 && !homeworkRules.some((r) => r.cadence === "DAILY") && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Reports will mostly repeat: this program's homework releases weekly, so most daily
                        reports describe the same round. Match the report cadence to the homework cadence,
                        or plan to send only on the days worth sending.
                    </Alert>
                )}
            </Box>

            <Box sx={{ opacity: s.enabled ? 1 : 0.5, pointerEvents: s.enabled ? "auto" : "none" }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Delivery</Typography>
                <RadioGroup value={s.delivery_mode} onChange={(e) => set({ delivery_mode: e.target.value })}>
                    <FormControlLabel
                        value="AUTO"
                        control={<Radio size="small" />}
                        label={<Typography variant="body2">Send automatically at the scheduled time</Typography>}
                    />
                    <FormControlLabel
                        value="REVIEW"
                        control={<Radio size="small" />}
                        label={
                            <Box>
                                <Typography variant="body2">Hold for review — I&apos;ll check and send them myself</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Drafts wait in “Report batches” below; nothing reaches students or parents until you click Send.
                                </Typography>
                            </Box>
                        }
                    />
                </RadioGroup>
            </Box>

            <Box sx={{ opacity: s.enabled ? 1 : 0.5, pointerEvents: s.enabled ? "auto" : "none" }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Recipients</Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <FormControlLabel
                        control={<Switch checked={s.email_student} onChange={(e) => set({ email_student: e.target.checked })} />}
                        label="Email the student"
                    />
                    <FormControlLabel
                        control={<Switch checked={s.email_parent} onChange={(e) => set({ email_parent: e.target.checked })} />}
                        label="Email the parent"
                    />
                    {s.email_parent && (
                        <FormControl size="small" sx={{ width: 240, ml: 4, mt: 0.5, mb: 1 }}>
                            <InputLabel>Which parent email</InputLabel>
                            <Select
                                label="Which parent email"
                                value={s.parent_email_mode}
                                onChange={(e) => set({ parent_email_mode: e.target.value })}
                            >
                                {PARENT_MODES.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <FormControlLabel
                        control={<Switch checked={s.email_admin} onChange={(e) => set({ email_admin: e.target.checked })} />}
                        label="Email admins / office"
                    />
                </Box>
                {s.email_admin && (
                    <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        label="Admin emails (comma-separated)"
                        placeholder="office@a-oneinstitute.com, ..."
                        value={s.admin_emails}
                        onChange={(e) => set({ admin_emails: e.target.value })}
                        helperText={`Who receives the per-program digest. Leave blank to use the default office address${defaultAdminEmail ? ` (${defaultAdminEmail})` : " (none configured)"}.`}
                    />
                )}
            </Box>

            <Box sx={{ opacity: s.enabled ? 1 : 0.5, pointerEvents: s.enabled ? "auto" : "none" }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Report content</Typography>
                <FormControlLabel
                    control={<Switch
                        checked={s.parent_report_version === "V2"}
                        onChange={(e) => set({ parent_report_version: e.target.checked ? "V2" : "V1" })}
                    />}
                    label="Add an AI “what to focus on” summary (V2)"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 6, mt: -0.5 }}>
                    Personalized from the student’s weak chapters &amp; test misses — added to the student email (English) and the parent email (Korean). Falls back to the standard email if unavailable.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                    <FormControlLabel
                        control={<Switch checked={content.include_homework} onChange={(e) => setC({ include_homework: e.target.checked })} />}
                        label="Include homework progress (current round)"
                    />
                    {content.include_homework && (
                        <FormControlLabel
                            sx={{ ml: 4 }}
                            control={<Switch checked={content.homework_time} onChange={(e) => setC({ homework_time: e.target.checked })} />}
                            label={<Typography variant="body2">Show time spent per chapter</Typography>}
                        />
                    )}
                    <FormControlLabel
                        control={<Switch
                            checked={content.exams_mode === "SELECTED"}
                            onChange={(e) => setC({ exams_mode: e.target.checked ? "SELECTED" : "ALL" })}
                        />}
                        label="Only selected exams (default: every program exam)"
                    />
                    {content.exams_mode === "SELECTED" && (
                        <FormControl size="small" fullWidth sx={{ ml: 4, maxWidth: 440 }}>
                            <InputLabel>Exams in the report</InputLabel>
                            <Select
                                multiple
                                value={content.exam_ids}
                                onChange={(e) => setC({ exam_ids: e.target.value })}
                                input={<OutlinedInput label="Exams in the report" />}
                                renderValue={(ids) => programExams.filter((x) => ids.includes(x.id)).map((x) => x.exam_title).join(", ")}
                            >
                                {programExams.map((x) => (
                                    <MenuItem key={x.id} value={x.id}>
                                        <Checkbox size="small" checked={content.exam_ids.includes(x.id)} />
                                        {x.exam_title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {programV2Tests.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            Library tests are included automatically once this program has some
                            (Tests tab → Library tests) — they appear as their own tables at the
                            end of the report, grouped by test type.
                        </Typography>
                    )}
                    {programV2Tests.length > 0 && (
                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
                            <FormControl size="small" sx={{ width: 260 }}>
                                <InputLabel>Library tests</InputLabel>
                                <Select
                                    label="Library tests"
                                    value={content.v2_mode}
                                    onChange={(e) => setC({ v2_mode: e.target.value })}
                                >
                                    <MenuItem value="ALL">All library tests (default)</MenuItem>
                                    <MenuItem value="SELECTED">Only selected tests</MenuItem>
                                    <MenuItem value="NONE">No library tests</MenuItem>
                                </Select>
                            </FormControl>
                            {content.v2_mode === "SELECTED" && (
                                <FormControl size="small" fullWidth sx={{ maxWidth: 440 }}>
                                    <InputLabel>Tests in the report</InputLabel>
                                    <Select
                                        multiple
                                        value={content.v2_test_ids}
                                        onChange={(e) => setC({ v2_test_ids: e.target.value })}
                                        input={<OutlinedInput label="Tests in the report" />}
                                        renderValue={(ids) => programV2Tests.filter((x) => ids.includes(x.id)).map((x) => x.test_title || `Test ${x.v2_test_id}`).join(", ")}
                                    >
                                        {programV2Tests.map((x) => (
                                            <MenuItem key={x.id} value={x.id}>
                                                <Checkbox size="small" checked={content.v2_test_ids.includes(x.id)} />
                                                {x.test_title || `Test ${x.v2_test_id}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    )}
                    {content.v2_mode === "SELECTED" && content.v2_test_ids.length === 0 && (
                        <Alert severity="warning">No library tests picked — this saves as “No library tests”.</Alert>
                    )}
                    {content.exams_mode === "SELECTED" && content.exam_ids.length === 0 && content.v2_mode === "NONE" && (
                        <Alert severity="warning">No exams or library tests selected — reports will carry no score table.</Alert>
                    )}
                </Box>
            </Box>

            {!s.email_student && !s.email_parent && !s.email_admin && s.enabled && (
                <Alert severity="warning">No recipients selected — reports will generate but won&apos;t be emailed.</Alert>
            )}

            <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
                    {saving ? "Saving…" : "Save report settings"}
                </Button>
                <Button variant="outlined" onClick={openPreview} startIcon={<VisibilityIcon />}>
                    Preview
                </Button>
            </Box>

            <ReportBatchesPanel programUuid={programUuid} />

            <Dialog open={previewOpen} onClose={closePreview} maxWidth="md" fullWidth>
                <DialogTitle>Report preview</DialogTitle>
                <DialogContent dividers>
                    {previewError ? (
                        <Alert
                            severity="error"
                            action={<Button color="inherit" size="small" onClick={loadEmailPreview}>Retry</Button>}
                        >
                            Couldn&apos;t load the preview. Please try again.
                        </Alert>
                    ) : (
                    <>
                    {previewData?.placeholder && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            No enrolled student with practice data yet — showing sample data so you can see the layout.
                        </Alert>
                    )}
                    {previewData?.sample && (
                        <Typography variant="caption" color="text.secondary">
                            Sample student: {previewData.sample.name}
                            {previewData.sample.ett_linked ? " (live ETT data)" : " (no ETT link — sample data)"}
                        </Typography>
                    )}
                    <Tabs value={previewTab} onChange={onPreviewTab} sx={{ mb: 1 }} variant="scrollable" scrollButtons="auto">
                        <Tab label="Student email" />
                        <Tab label="Parent email" />
                        <Tab label="Admin / office email" />
                        <Tab label="PDF" />
                    </Tabs>

                    {previewLoading ? (
                        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                    ) : (
                        <>
                            {previewTab === 0 && previewData && (
                                <Box>
                                    <Typography variant="body2"><strong>Subject:</strong> {previewData.student_email.subject}</Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Sends to:</strong>{" "}
                                        {studentRecips.length ? studentRecips.join(", ") : "— (student email off, or no address on the sample student)"}
                                    </Typography>
                                    <iframe
                                        title="Student email preview"
                                        srcDoc={previewData.student_email.html}
                                        style={{ width: "100%", height: 360, border: "1px solid #ddd", borderRadius: 4 }}
                                    />
                                </Box>
                            )}
                            {previewTab === 1 && previewData && (
                                <Box>
                                    <Typography variant="body2"><strong>Subject:</strong> {previewData.parent_email.subject}</Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Sends to:</strong>{" "}
                                        {parentRecips.length ? parentRecips.join(", ") : "— (parent email off, or no parent address on the sample student)"}
                                    </Typography>
                                    <iframe
                                        title="Parent email preview"
                                        srcDoc={previewData.parent_email.html}
                                        style={{ width: "100%", height: 360, border: "1px solid #ddd", borderRadius: 4 }}
                                    />
                                </Box>
                            )}
                            {previewTab === 2 && previewData && (
                                <Box>
                                    <Typography variant="body2"><strong>Subject:</strong> {previewData.admin_email.subject}</Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}><strong>Sends to:</strong> {adminRecipientsText}</Typography>
                                    <iframe
                                        title="Admin email preview"
                                        srcDoc={previewData.admin_email.html}
                                        style={{ width: "100%", height: 360, border: "1px solid #ddd", borderRadius: 4 }}
                                    />
                                </Box>
                            )}
                            {previewTab === 3 && (
                                pdfLoading ? (
                                    <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                                ) : pdfUrl ? (
                                    <iframe title="PDF preview" src={pdfUrl} style={{ width: "100%", height: 500, border: "1px solid #ddd", borderRadius: 4 }} />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>PDF preview unavailable.</Typography>
                                )
                            )}
                        </>
                    )}
                    </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePreview}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReportScheduleSection;
